import { useState, useMemo, useEffect, useCallback } from 'react'
import { PlatformUserDomainRecord } from '../../../../models/domain/platform-user-domain-record'
import type { SearchIndexEntry, RankedDomain } from '../types'
import { filterDomains } from '../utils/ranking'

/**
 * useSearch — encapsulates search index construction, debounced query state
 * (150 ms), and filtering/ranking for the domain selector.
 *
 * Delegates ranking to `utils/ranking.ts`.
 *
 * @param domains - The full domain list (may be null while loading)
 * @returns search index, query state, query setter, and filtered-domains helper
 */
export function useSearch(domains: PlatformUserDomainRecord[] | null): {
    searchIndex: SearchIndexEntry[]
    pinnedSearchIndex: (pinned: number[]) => SearchIndexEntry[]
    debouncedQuery: string
    query: string
    setQuery: (q: string) => void
    filteredDomains: (activeTab: 'all' | 'pinned', pinned: number[]) => RankedDomain[]
} {
    // ========================================================================
    // State
    // ========================================================================

    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')

    // ========================================================================
    // Debounce effect (150 ms)
    // ========================================================================

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query)
        }, 150)

        return () => {
            clearTimeout(timer)
        }
    }, [query])

    // ========================================================================
    // Search index — normalised fields for fast matching
    // ========================================================================

    const searchIndex = useMemo((): SearchIndexEntry[] => {
        if (!domains || domains.length === 0) {
            return []
        }

        // Single pass through domains array — O(n) complexity
        return domains.map((domain) => ({
            domain,
            normalizedId: domain.id.toString().toLowerCase(),
            normalizedName: domain.name.toLowerCase(),
            normalizedDisplayName: domain.displayName.toLowerCase(),
            normalizedAccountName: domain.accountName.toLowerCase(),
            accountId: domain.accountId,
        }))
    }, [domains])

    // ========================================================================
    // Pinned search index — returns a subset filtered to pinned domain ids
    // ========================================================================

    const pinnedSearchIndex = useCallback(
        (pinned: number[]): SearchIndexEntry[] => {
            const pinnedSet = new Set(pinned)
            return searchIndex.filter((entry) => pinnedSet.has(entry.domain.id))
        },
        [searchIndex],
    )

    // ========================================================================
    // Filtered domains — delegates to ranking.ts
    // ========================================================================

    const filteredDomainsFn = useCallback(
        (activeTab: 'all' | 'pinned', pinned: number[]): RankedDomain[] => {
            const index = activeTab === 'pinned' ? pinnedSearchIndex(pinned) : searchIndex
            return filterDomains(index, debouncedQuery)
        },
        [searchIndex, pinnedSearchIndex, debouncedQuery],
    )

    return {
        searchIndex,
        pinnedSearchIndex,
        debouncedQuery,
        query,
        setQuery,
        filteredDomains: filteredDomainsFn,
    }
}
